import type {
	Client,
	ClientOptions,
	ExecutionResult,
	Sink,
	SubscribePayload,
} from "graphql-ws";

/**
 * Fault-tolerant wrapper around a `graphql-ws` client, shared by every Hub
 * realtime consumer (Staff, Consumer, print service).
 *
 * `graphql-ws` alone reconnects the socket, but it
 * - never acts on a missing pong, so a half-open socket (sleep/wake, NAT drop)
 *   stays "connected" until the OS gives up;
 * - waits forever for `connection_ack` by default;
 * - ends a subscription for good when the server rejects it (e.g. an expired
 *   token after a reconnect) or closes with a "fatal" code.
 *
 * This wrapper adds a pong deadline, an ack timeout, jittered backoff,
 * wake/timer-gap probes, per-subscription re-subscription and a reconnect hook
 * so callers can reconcile state they may have missed while disconnected.
 */

interface WebSocketLike {
	readyState: number;
	send: (data: string) => void;
}

const WS_OPEN = 1;
const PING_MESSAGE = JSON.stringify({ type: "ping" });
const TOKEN_EXPIRED = "TOKEN_EXPIRED";
/** A subscription that lived this long restarts its retry backoff. */
const STABLE_SUBSCRIPTION_MS = 60_000;

export type WakeSignalSource = (wake: () => void) => () => void;

export interface BackoffOptions {
	baseMs: number;
	maxMs: number;
}

export interface ResilientClientOptions
	extends Omit<
		ClientOptions,
		| "keepAlive"
		| "retryAttempts"
		| "retryWait"
		| "shouldRetry"
		| "connectionAckWaitTimeout"
	> {
	/** `graphql-ws` `createClient`, injected so this module stays runtime-agnostic. */
	createClient: (options: ClientOptions) => Client;
	pingIntervalMs?: number;
	/** Terminate the socket when no message arrives this long after a ping. */
	pongTimeoutMs?: number;
	connectionAckTimeoutMs?: number;
	backoff?: BackoffOptions;
	/** Treat a timer tick this late as a suspend/resume and probe the socket. */
	timerGapMs?: number;
	/** Platform events that should probe the socket or cut a retry wait short. */
	wakeSignals?: WakeSignalSource;
	/** Delay before reporting a disconnect, so forced reconnects do not flicker. */
	disconnectGraceMs?: number;
	onConnectionChange?: (connected: boolean) => void;
	/** Runs after every reconnect; events published while offline were missed. */
	onReconnected?: () => void;
	/**
	 * Runs when the Hub rejects a subscription with `TOKEN_EXPIRED`. Resolve
	 * `true` once fresh credentials are available (the socket is then
	 * re-established), `false` to end the affected subscriptions.
	 * Without it, expired-token rejections end the subscription.
	 */
	onAuthExpired?: () => Promise<boolean>;
	/** Reports every server-side end of a subscription before it is retried. */
	onSubscriptionInterrupted?: (
		reason: unknown,
		payload: SubscribePayload,
	) => void;
}

export interface ResilientClient extends Client {
	/** Synchronous: retries stop and the socket drops immediately. */
	dispose(): void;
	/** Probe the socket now; skip any pending retry wait. */
	wake(): void;
	/** Drop the current socket and reconnect with fresh connection params. */
	reconnect(): void;
}

export const DEFAULT_BACKOFF: BackoffOptions = { baseMs: 1_000, maxMs: 15_000 };

/** Exponential backoff with equal jitter: [delay / 2, delay]. */
export const getBackoffDelay = (
	attempt: number,
	{ baseMs, maxMs }: BackoffOptions = DEFAULT_BACKOFF,
	random: () => number = Math.random,
) => {
	const delay = Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt));
	return Math.round(delay / 2 + (random() * delay) / 2);
};

const getErrorCodes = (reason: unknown): string[] => {
	if (!Array.isArray(reason)) return [];
	return reason.flatMap((error: unknown) => {
		const code = (error as { extensions?: { code?: unknown } } | null)
			?.extensions?.code;
		return typeof code === "string" ? [code] : [];
	});
};

export const isTokenExpiredReason = (reason: unknown) =>
	getErrorCodes(reason).includes(TOKEN_EXPIRED);

interface BrowserEventTarget {
	addEventListener(type: string, listener: () => void): void;
	removeEventListener(type: string, listener: () => void): void;
}

/** Browser events after which a socket is likely dead or a retry should run now. */
export const browserWakeSignals: WakeSignalSource = (wake) => {
	// Structural types keep this package free of the DOM lib.
	const { window: win, document: doc } = globalThis as {
		window?: BrowserEventTarget;
		document?: BrowserEventTarget & { visibilityState: string };
	};
	if (!win || !doc) return () => undefined;

	const onVisibilityChange = () => {
		if (doc.visibilityState === "visible") wake();
	};
	win.addEventListener("online", wake);
	win.addEventListener("pageshow", wake);
	doc.addEventListener("resume", wake);
	doc.addEventListener("visibilitychange", onVisibilityChange);

	return () => {
		win.removeEventListener("online", wake);
		win.removeEventListener("pageshow", wake);
		doc.removeEventListener("resume", wake);
		doc.removeEventListener("visibilitychange", onVisibilityChange);
	};
};

export const createResilientClient = ({
	createClient,
	pingIntervalMs = 5_000,
	pongTimeoutMs = 10_000,
	connectionAckTimeoutMs = 10_000,
	backoff = DEFAULT_BACKOFF,
	timerGapMs = 15_000,
	wakeSignals,
	disconnectGraceMs = 5_000,
	onConnectionChange,
	onReconnected,
	onAuthExpired,
	onSubscriptionInterrupted,
	on,
	...clientOptions
}: ResilientClientOptions): ResilientClient => {
	let disposed = false;
	let activeSocket: WebSocketLike | null = null;
	let hasConnected = false;
	let reportedConnected: boolean | undefined;
	let pongDeadline: ReturnType<typeof setTimeout> | undefined;
	let disconnectTimer: ReturnType<typeof setTimeout> | undefined;
	let skipRetryWait: (() => void) | null = null;
	let authRecovery: Promise<boolean> | null = null;
	const activeSubscriptions = new Set<() => void>();

	const reportConnection = (connected: boolean) => {
		clearTimeout(disconnectTimer);
		disconnectTimer = undefined;
		if (disposed || reportedConnected === connected) return;
		reportedConnected = connected;
		onConnectionChange?.(connected);
	};

	const scheduleDisconnectReport = () => {
		if (disconnectTimer || reportedConnected === false) return;
		disconnectTimer = setTimeout(
			() => reportConnection(false),
			disconnectGraceMs,
		);
	};

	const clearPongDeadline = () => {
		clearTimeout(pongDeadline);
		pongDeadline = undefined;
	};

	const armPongDeadline = () => {
		if (pongDeadline) return;
		pongDeadline = setTimeout(() => {
			pongDeadline = undefined;
			base.terminate();
		}, pongTimeoutMs);
	};

	const base = createClient({
		...clientOptions,
		keepAlive: pingIntervalMs,
		connectionAckWaitTimeout: connectionAckTimeoutMs,
		retryAttempts: Infinity,
		shouldRetry: () => true,
		retryWait: (retries) =>
			new Promise<void>((resolve) => {
				const done = () => {
					clearTimeout(timer);
					skipRetryWait = null;
					resolve();
				};
				const timer = setTimeout(done, getBackoffDelay(retries, backoff));
				skipRetryWait = done;
			}),
		on: {
			...on,
			connected: (socket, payload) => {
				activeSocket = socket as WebSocketLike;
				reportConnection(true);
				if (hasConnected) onReconnected?.();
				hasConnected = true;
				on?.connected?.(socket, payload);
			},
			ping: (received, payload) => {
				if (!received) armPongDeadline();
				on?.ping?.(received, payload);
			},
			message: (message) => {
				clearPongDeadline();
				on?.message?.(message);
			},
			closed: (event) => {
				activeSocket = null;
				clearPongDeadline();
				scheduleDisconnectReport();
				on?.closed?.(event);
			},
			error: (error) => {
				scheduleDisconnectReport();
				on?.error?.(error);
			},
		},
	});

	const reconnect = () => {
		if (disposed) return;
		clearPongDeadline();
		base.terminate();
	};

	const wake = () => {
		if (disposed) return;
		if (skipRetryWait) return skipRetryWait();
		const socket = activeSocket;
		if (socket?.readyState !== WS_OPEN) return;
		try {
			socket.send(PING_MESSAGE);
			armPongDeadline();
		} catch {
			reconnect();
		}
	};

	const recoverAuth = () => {
		if (!onAuthExpired) return Promise.resolve(false);
		authRecovery ??= onAuthExpired()
			.catch(() => false)
			.then((recovered) => {
				if (recovered) reconnect();
				return recovered;
			})
			.finally(() => {
				authRecovery = null;
			});
		return authRecovery;
	};

	const tick = Math.min(5_000, timerGapMs);
	let previousTick = Date.now();
	const timerGapMonitor = setInterval(() => {
		const now = Date.now();
		const elapsed = now - previousTick;
		previousTick = now;
		if (elapsed - tick >= timerGapMs) wake();
	}, tick);
	(timerGapMonitor as { unref?: () => void }).unref?.();

	const unsubscribeWakeSignals = wakeSignals?.(wake);

	const subscribe = <Data = Record<string, unknown>, Extensions = unknown>(
		payload: SubscribePayload,
		sink: Sink<ExecutionResult<Data, Extensions>>,
	) => {
		let stopped = false;
		let attempt = 0;
		let startedAt = 0;
		let retryTimer: ReturnType<typeof setTimeout> | undefined;
		let unsubscribe: (() => void) | undefined;

		const stop = (finish: () => void) => {
			if (stopped) return;
			stopped = true;
			activeSubscriptions.delete(complete);
			clearTimeout(retryTimer);
			finish();
		};
		const complete = () =>
			stop(() => {
				unsubscribe?.();
				sink.complete();
			});

		const scheduleRetry = () => {
			retryTimer = setTimeout(start, getBackoffDelay(attempt++, backoff));
		};

		const handleEnd = async (reason: unknown) => {
			if (stopped) return;
			if (disposed) return stop(() => sink.complete());
			onSubscriptionInterrupted?.(reason, payload);
			if (Date.now() - startedAt >= STABLE_SUBSCRIPTION_MS) attempt = 0;

			if (isTokenExpiredReason(reason) && !(await recoverAuth())) {
				return stop(() => sink.error(reason));
			}
			if (!stopped && !disposed) scheduleRetry();
		};

		function start() {
			if (stopped || disposed) return;
			let rejection: unknown;
			startedAt = Date.now();
			unsubscribe = base.subscribe<Data, Extensions>(payload, {
				next: (value) => {
					// A subscription the server rejects arrives as one errors-only
					// result followed by `complete`.
					if (value.errors?.length && !value.data) rejection = value.errors;
					else attempt = 0;
					sink.next(value);
				},
				error: (error) => void handleEnd(error),
				complete: () => void handleEnd(rejection ?? null),
			});
		}

		activeSubscriptions.add(complete);
		start();

		return () =>
			stop(() => {
				unsubscribe?.();
			});
	};

	const iterate = <Data = Record<string, unknown>, Extensions = unknown>(
		payload: SubscribePayload,
	): AsyncIterableIterator<ExecutionResult<Data, Extensions>> => {
		type Result = ExecutionResult<Data, Extensions>;
		const pending: Result[] = [];
		let done = false;
		let failure: { error: unknown } | null = null;
		let notify: (() => void) | null = null;
		const wakeReader = () => {
			notify?.();
			notify = null;
		};

		const unsubscribe = subscribe<Data, Extensions>(payload, {
			next: (value) => {
				pending.push(value);
				wakeReader();
			},
			error: (error) => {
				failure = { error };
				done = true;
				wakeReader();
			},
			complete: () => {
				done = true;
				wakeReader();
			},
		});

		const finish = (): Promise<IteratorResult<Result>> => {
			done = true;
			unsubscribe();
			wakeReader();
			return Promise.resolve({ done: true, value: undefined });
		};

		const iterator: AsyncIterableIterator<Result> = {
			next: async () => {
				while (!pending.length && !done) {
					await new Promise<void>((resolve) => (notify = resolve));
				}
				if (pending.length) return { done: false, value: pending.shift()! };
				if (failure) throw failure.error;
				return { done: true, value: undefined };
			},
			return: finish,
			throw: (error) => {
				failure ??= { error };
				return finish();
			},
			[Symbol.asyncIterator]: () => iterator,
		};
		return iterator;
	};

	const dispose = () => {
		if (disposed) return;
		disposed = true;
		clearInterval(timerGapMonitor);
		clearPongDeadline();
		clearTimeout(disconnectTimer);
		unsubscribeWakeSignals?.();
		for (const complete of [...activeSubscriptions]) complete();
		// With no subscriptions left, a pending retry resolves without reconnecting.
		skipRetryWait?.();
		// `dispose` marks the client so retries stop; `terminate` drops the socket
		// now instead of waiting for a pending connection attempt to settle.
		void Promise.resolve(base.dispose()).catch(() => undefined);
		base.terminate();
	};

	return {
		on: base.on.bind(base),
		subscribe,
		iterate,
		terminate: reconnect,
		dispose,
		wake,
		reconnect,
	};
};
